import { useEffect, useMemo, useState } from "react";
import type { BillOut } from "../../../../api/bills";
import { useConfiguration } from "../../../../hooks/useConfiguration";
import { useAppDispatch } from "../../../../store/hooks";
import { setSelectedBillId } from "../../../../store";
import { sortBillsForTableList } from "./tableOrder";
import { mergeClosingBills, useClosingBills } from "./payment/closingBills";

interface TableListBillsOptions {
  /** Отговорът на `GET /bills`. */
  bills: BillOut[];
  isTablesView: boolean;
  billsFetching: boolean;
  selectedBillId: number | null;
}

/**
 * Масите, както ги вижда списъкът на „Маркиране" — изваден от `Ordering.tsx`:
 *
 * - закритите без бон: показани/скрити по настройката на ресторанта
 *   (`nonFiscalDisplayMode`; ALT+F1 при „при поискване");
 * - масите, чието закриване още чака принтера: стоят на мястото си, каквато и
 *   да е настройката (`payment/closingBills.ts`);
 * - авто-изборът на първата маса, щом избраната вече я няма.
 */
export function useTableListBills({
  bills,
  isTablesView,
  billsFetching,
  selectedBillId,
}: TableListBillsOptions) {
  const dispatch = useAppDispatch();
  const { configuration, isLoaded: configurationLoaded } = useConfiguration();
  const orderingTableOrder = configuration.orderingTableOrder;
  const { ownClosingBills, closingBillIds } = useClosingBills();

  const tableBills = useMemo(
    () => mergeClosingBills(bills, ownClosingBills),
    [bills, ownClosingBills],
  );

  // Докато /user/info още не е зареден, масите се показват (иначе мигат).
  const nonFiscalMode = configurationLoaded
    ? configuration.nonFiscalDisplayMode
    : "always";
  const [showClosedTables, setShowClosedTables] = useState(true);

  // Sync showClosedTables initial state based on mode
  useEffect(() => {
    if (nonFiscalMode === "always") {
      setShowClosedTables(true);
    } else if (nonFiscalMode === "on_demand") {
      setShowClosedTables(false);
    }
  }, [nonFiscalMode]);

  // ALT+F1 toggle for on_demand mode
  useEffect(() => {
    if (nonFiscalMode !== "on_demand") return;

    const handleAltF1 = (e: KeyboardEvent) => {
      if (e.altKey && e.key === "F1") {
        e.preventDefault();
        setShowClosedTables((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleAltF1);
    return () => window.removeEventListener("keydown", handleAltF1);
  }, [nonFiscalMode]);

  // Същата подредба като в TableList (закритите най-отдолу; хронологично или
  // по номер/име според настройката на ресторанта) — оттук се взима ПЪРВАТА
  // маса за авто-избор, затова двете трябва да съвпадат.
  const sortedBills = useMemo(
    () =>
      sortBillsForTableList(
        tableBills,
        showClosedTables,
        orderingTableOrder,
        closingBillIds,
      ),
    [tableBills, showClosedTables, orderingTableOrder, closingBillIds],
  );

  // Автоматично селектиране на първата маса при рефреш (винаги първата в списъка)
  useEffect(() => {
    if (!isTablesView) {
      return;
    }

    // Изчакваме да завърши refetch-ът (напр. след създаване на нова маса)
    if (billsFetching) {
      return;
    }

    if (sortedBills.length === 0) {
      if (selectedBillId !== null) {
        dispatch(setSelectedBillId(null));
      }
      return;
    }

    // Селектираме първата маса само ако няма валидна селекция
    const isCurrentSelectionValid = sortedBills.some(
      (b) => b.id === selectedBillId,
    );
    if (!isCurrentSelectionValid) {
      dispatch(setSelectedBillId(sortedBills[0].id));
    }
  }, [isTablesView, sortedBills, selectedBillId, dispatch, billsFetching]);

  return { tableBills, closingBillIds, showClosedTables };
}
